import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cobrosService } from '../services/cobrosService';
import {
  Cobros,
  TipoMoneda,
  IniciarCobroData,
  EditarMontoCobroData,
  CobroFormData,
  CobroEstado,
  CobroMoneda,
} from '../types/cobros';

/**
 * Calcular estado del cobro (totales, porcentajes, etc.)
 */
export const calcularEstadoCobro = (
  cobroMoneda: CobroMoneda | null
): CobroEstado => {
  if (!cobroMoneda || !cobroMoneda.iniciado) {
    return {
      totalCobrado: 0,
      porcentajeCobrado: 0,
      saldoPendiente: cobroMoneda?.montoTotal || 0,
      estaCompleto: false,
    };
  }

  const totalCobrado = cobroMoneda.cobros.reduce(
    (sum, cobro) => sum + cobro.monto,
    0
  );
  const porcentajeCobrado = cobroMoneda.montoTotal > 0
    ? (totalCobrado / cobroMoneda.montoTotal) * 100
    : 0;
  const saldoPendiente = cobroMoneda.montoTotal - totalCobrado;

  return {
    totalCobrado,
    porcentajeCobrado: Math.min(porcentajeCobrado, 100),
    saldoPendiente: Math.max(saldoPendiente, 0),
    estaCompleto: saldoPendiente <= 0,
  };
};

// Keys del módulo de contabilidad que deben invalidarse ante cambios en cobros
// de otros trámites (cruce bidireccional).
const CONTABILIDAD_KEYS_TO_INVALIDATE = [
  ['contabilidad', 'cobros-pendientes'],
  ['contabilidad', 'movimientos'],
  ['contabilidad', 'kpis'],
  ['contabilidad', 'cajas'],
  ['contabilidad', 'chart'],
];

/**
 * Hook principal para gestionar cobros
 */
export const useCobros = (otroTramiteId: string) => {
  const queryClient = useQueryClient();
  const queryKey = ['cobros', otroTramiteId];

  /** Invalida la lista principal de otros-tramites y las queries de contabilidad. */
  const invalidarTodo = () => {
    queryClient.invalidateQueries({ queryKey });
    queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
    CONTABILIDAD_KEYS_TO_INVALIDATE.forEach((qk) => {
      queryClient.invalidateQueries({ queryKey: qk });
    });
  };

  // Query para obtener los cobros
  const {
    data: cobros,
    isLoading,
    error,
    refetch,
  } = useQuery<Cobros>({
    queryKey,
    queryFn: () => cobrosService.getCobros(otroTramiteId),
    staleTime: 1000 * 60 * 5, // 5 minutos
  });

  // Mutation para iniciar cobro (no impacta contabilidad — solo inicia el presupuesto)
  const iniciarMutation = useMutation({
    mutationFn: ({
      moneda,
      data,
    }: {
      moneda: TipoMoneda;
      data: IniciarCobroData;
    }) => cobrosService.iniciarCobro(otroTramiteId, moneda, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
    },
  });

  // Mutation para registrar cobro — impacta contabilidad vía observer
  const registrarCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      data,
    }: {
      moneda: TipoMoneda;
      data: CobroFormData;
    }) => cobrosService.registrarCobro(otroTramiteId, moneda, data),
    onSuccess: invalidarTodo,
  });

  // Mutation para registrar cobro total — impacta contabilidad vía observer
  const registrarCobroTotalMutation = useMutation({
    mutationFn: ({ moneda, cajaId }: { moneda: TipoMoneda; cajaId?: number | null }) =>
      cobrosService.registrarCobroTotal(otroTramiteId, moneda, cajaId),
    onSuccess: invalidarTodo,
  });

  // Mutation para actualizar cobro — impacta contabilidad vía observer
  const actualizarCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      cobroId,
      data,
    }: {
      moneda: TipoMoneda;
      cobroId: string;
      data: CobroFormData;
    }) => cobrosService.actualizarCobro(otroTramiteId, moneda, cobroId, data),
    onSuccess: invalidarTodo,
  });

  // Mutation para eliminar cobro — impacta contabilidad vía observer
  const eliminarCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      cobroId,
    }: {
      moneda: TipoMoneda;
      cobroId: string;
    }) => cobrosService.eliminarCobro(otroTramiteId, moneda, cobroId),
    onSuccess: invalidarTodo,
  });

  // Mutation para editar monto del cobro
  const editarMontoCobroMutation = useMutation({
    mutationFn: ({
      moneda,
      data,
    }: {
      moneda: TipoMoneda;
      data: EditarMontoCobroData;
    }) => cobrosService.editarMontoCobro(otroTramiteId, moneda, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['otros-tramites'] });
    },
  });

  // Mutation para eliminar (resetear) cobros — impacta contabilidad
  const eliminarCobrosMutation = useMutation({
    mutationFn: (moneda: TipoMoneda) =>
      cobrosService.eliminarCobros(otroTramiteId, moneda),
    onSuccess: invalidarTodo,
  });

  // Calcular estados para cada moneda
  const estadoARS = calcularEstadoCobro(cobros?.ars || null);
  const estadoUSD = calcularEstadoCobro(cobros?.usd || null);

  return {
    // Data
    cobros,
    estadoARS,
    estadoUSD,

    // Loading states
    isLoading,
    error,

    // Mutations
    iniciarCobro: iniciarMutation.mutateAsync,
    registrarCobro: registrarCobroMutation.mutateAsync,
    registrarCobroTotal: registrarCobroTotalMutation.mutateAsync,
    actualizarCobro: actualizarCobroMutation.mutateAsync,
    eliminarCobro: eliminarCobroMutation.mutateAsync,
    editarMontoCobro: editarMontoCobroMutation.mutateAsync,
    eliminarCobros: eliminarCobrosMutation.mutateAsync,

    // Mutation states
    isIniciando: iniciarMutation.isPending,
    isRegistrandoCobro: registrarCobroMutation.isPending,
    isActualizandoCobro: actualizarCobroMutation.isPending,
    isEliminandoCobro: eliminarCobroMutation.isPending,
    isEditandoMonto: editarMontoCobroMutation.isPending,
    isEliminandoCobros: eliminarCobrosMutation.isPending,

    // Utils
    refetch,
  };
};
